import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { OpsOverview } from '@wallflower/shared';
import { ApiError } from '../lib/api.ts';
import { clearOpsToken, opsApi, readOpsToken, writeOpsToken } from '../lib/opsApi.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';
import OpsSessionList from '../components/OpsSessionList.tsx';
import NotFound from './NotFound.tsx';

/** The dashboard refreshes on a timer; the hub has no instance-wide room to listen to. */
const POLL_INTERVAL_MS = 10_000;

export default function Ops() {
  const [signedIn, setSignedIn] = useState(() => readOpsToken() !== null);
  const [overview, setOverview] = useState<OpsOverview | null>(null);
  const [disabled, setDisabled] = useState(false);
  // Undefined once disabled, so the NotFound this falls through to keeps its own
  // title: a child's effect runs before its parent's, so naming the page here
  // unconditionally would overwrite it.
  useDocumentTitle(disabled ? undefined : 'Operator');
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    try {
      const result = await opsApi.overview();
      setOverview(result);
      setRefreshedAt(Date.now());
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setSignedIn(false);
        setOverview(null);
        return;
      }
      // A 404 here means the operator never set OPS_PASSPHRASE on this instance.
      if (err instanceof ApiError && err.status === 404) {
        setDisabled(true);
        return;
      }
      setError('Could not load the instance.');
    }
  }, []);

  useEffect(() => {
    if (!signedIn) return;
    void refresh();
    const timer = setInterval(() => void refresh(), POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [signedIn, refresh]);

  const signOut = useCallback(() => {
    void opsApi.logout().catch(() => undefined);
    clearOpsToken();
    setSignedIn(false);
    setOverview(null);
  }, []);

  // A disabled dashboard looks exactly like a URL that was never a page.
  if (disabled) return <NotFound />;

  if (!signedIn) {
    return (
      <OpsLogin
        onSignedIn={() => {
          setSignedIn(true);
          setError(null);
        }}
        onUnavailable={() => setDisabled(true)}
      />
    );
  }

  return (
    <main className="mx-auto w-full max-w-6xl flex-1 px-4 pb-16 sm:px-6">
      <header className="sticky top-0 z-30 -mx-4 mb-6 flex items-center gap-3 border-b border-edge bg-dusk/90 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
        <div className="min-w-0">
          <p className="eyebrow">Instance</p>
          <h1 className="font-display truncate text-xl font-semibold">Everything on this server</h1>
        </div>
        <span className="ml-auto shrink-0 text-xs text-muted">
          {refreshedAt ? `Updated ${new Date(refreshedAt).toLocaleTimeString()}` : 'Loading…'}
        </span>
        <button className="btn btn-ghost shrink-0 px-3 py-2 text-sm" onClick={() => void refresh()}>
          Refresh
        </button>
        <button className="btn btn-ghost shrink-0 px-3 py-2 text-sm" onClick={signOut}>
          Sign out
        </button>
      </header>

      {error ? (
        <p role="alert" className="panel mb-6 p-3 text-sm text-danger">
          {error}
        </p>
      ) : null}

      {overview ? (
        <OpsSessionList overview={overview} onChanged={() => void refresh()} />
      ) : (
        <p className="text-muted">Reading the instance…</p>
      )}
    </main>
  );
}

function OpsLogin({
  onSignedIn,
  onUnavailable,
}: {
  onSignedIn: () => void;
  onUnavailable: () => void;
}) {
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await opsApi.login(passphrase);
      writeOpsToken(result);
      onSignedIn();
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        onUnavailable();
        return;
      }
      setError(describeOpsError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto grid w-full max-w-md flex-1 place-items-center px-6">
      <form onSubmit={onSubmit} className="panel grid w-full gap-5 p-6 sm:p-8">
        <div>
          <p className="eyebrow">Operator</p>
          <h1 className="font-display mt-2 text-2xl font-semibold">Sign in to this instance</h1>
          <p className="mt-2 text-sm text-muted">
            The instance passphrase, not an event one. It opens every event on this server.
          </p>
        </div>

        <div className="grid gap-2">
          <label className="eyebrow" htmlFor="ops-passphrase">
            Instance passphrase
          </label>
          <input
            id="ops-passphrase"
            className="field"
            type="password"
            autoComplete="current-password"
            value={passphrase}
            onChange={(e) => setPassphrase(e.target.value)}
            required
            autoFocus
          />
        </div>

        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}

        <button className="btn btn-primary justify-self-start" disabled={busy || !passphrase}>
          {busy ? 'Checking…' : 'Sign in'}
        </button>
      </form>
    </main>
  );
}

/** Sign-in is rate limited hard, so the wait is the thing worth saying. */
function describeOpsError(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : 'Something went wrong.';
  }
  if (err.status === 403) return 'That passphrase is not right.';
  if (err.status === 429) {
    const seconds = err.retryAfterSeconds ?? 0;
    const wait =
      seconds >= 90
        ? `about ${Math.round(seconds / 60)} minutes`
        : seconds > 0
          ? `${seconds} seconds`
          : 'a few minutes';
    return `Too many sign-in attempts. Try again in ${wait}.`;
  }
  return err.message;
}
